import { useState, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod/v3';
import { toast } from 'sonner';
import { Link, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from '@/components/ui/drawer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAddLink } from '@/hooks/useAddLink';
import { useIsMobile } from '@/hooks/useMediaQuery';
import { trackEvent, trackCriticalAction, AnalyticsEvent } from '@/lib/analytics';

const addLinkSchema = z.object({
  url: z.string().url('Please enter a valid URL'),
});

type AddLinkForm = z.infer<typeof addLinkSchema>;

interface AddLinkDialogProps {
  trigger?: React.ReactNode;
  onSuccess?: () => void;
}

export function AddLinkDialog({ trigger, onSuccess }: AddLinkDialogProps) {
  const [open, setOpen] = useState(false);
  const submitStartTime = useRef<number>(0);
  const isMobile = useIsMobile();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<AddLinkForm>({
    resolver: zodResolver(addLinkSchema),
    defaultValues: {
      url: '',
    },
  });

  const addLink = useAddLink({
    onSuccess: (link) => {
      const duration = Date.now() - submitStartTime.current;
      trackCriticalAction('add_link', true, {
        linkId: link.id,
        url: link.url,
        durationMs: duration,
      });
      toast.success('Link saved! Processing will begin shortly.');
      setOpen(false);
      reset();
      onSuccess?.();
    },
    onError: (error) => {
      const duration = Date.now() - submitStartTime.current;
      trackCriticalAction('add_link', false, {
        error: error.message,
        durationMs: duration,
      });
      toast.error(error.message || 'Failed to save link');
    },
  });

  const handleOpenChange = (newOpen: boolean) => {
    if (newOpen) {
      trackEvent(AnalyticsEvent.ADD_LINK_DIALOG_OPEN);
    }
    setOpen(newOpen);
  };

  const onSubmit = (data: AddLinkForm) => {
    submitStartTime.current = Date.now();
    trackEvent(AnalyticsEvent.ADD_LINK_SUBMITTED, {
      urlDomain: new URL(data.url).hostname,
    });
    addLink.mutate(data);
  };

  const defaultTrigger = (
    <Button>
      <Link className="mr-2 h-4 w-4" />
      Add Link
    </Button>
  );

  const formContent = (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="my-6 space-y-4">
        <div className="space-y-2">
          <Label htmlFor="url">URL</Label>
          <Input
            id="url"
            type="url"
            placeholder="https://example.com/article"
            autoComplete="off"
            autoFocus
            {...register('url')}
          />
          {errors.url && <p className="text-sm text-destructive">{errors.url.message}</p>}
        </div>
      </div>

      {isMobile ? (
        <DrawerFooter>
          <Button type="submit" disabled={addLink.isPending} className="w-full">
            {addLink.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              'Save Link'
            )}
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={() => setOpen(false)}
            disabled={addLink.isPending}
            className="w-full"
          >
            Cancel
          </Button>
        </DrawerFooter>
      ) : (
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => setOpen(false)}
            disabled={addLink.isPending}
          >
            Cancel
          </Button>
          <Button type="submit" disabled={addLink.isPending}>
            {addLink.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              'Save Link'
            )}
          </Button>
        </DialogFooter>
      )}
    </form>
  );

  // Use Drawer (bottom sheet) on mobile, Dialog (centered modal) on desktop
  if (isMobile) {
    return (
      <Drawer open={open} onOpenChange={handleOpenChange}>
        <DrawerTrigger asChild>{trigger || defaultTrigger}</DrawerTrigger>
        <DrawerContent>
          <DrawerHeader>
            <DrawerTitle>Save a Link</DrawerTitle>
            <DrawerDescription>
              Paste a URL to save it. We'll extract the content and generate a summary.
            </DrawerDescription>
          </DrawerHeader>
          {formContent}
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>{trigger || defaultTrigger}</DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Save a Link</DialogTitle>
          <DialogDescription>
            Paste a URL to save it. We'll extract the content and generate a summary.
          </DialogDescription>
        </DialogHeader>
        {formContent}
      </DialogContent>
    </Dialog>
  );
}
